import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { Maybe } from 'types/graphqlTypes'
import { titleCase } from 'utils/titleCase'

export const ISO_8601_ALL_DAYS = [1, 2, 3, 4, 5, 6, 7] as const
export const ISO_8601_WEEK_DAYS = [1, 2, 3, 4, 5] as const
export const ISO_8601_WEEKEND_DAYS = [6, 7] as const

const DELIM = ' · ' as const

/** A type which could conceivably converted by moment.js */
type TDateLike = string | Date | moment.Moment

/** A type which represents a valid ISO-8601 day of week value. */
type TIso8601Day = typeof ISO_8601_ALL_DAYS[number]

/** A TypeScript type guard to determine if a value is a date-like value. */
export const isDateLike = (maybeDate: unknown): maybeDate is TDateLike =>
  maybeDate instanceof Date ||
  moment.isMoment(maybeDate) ||
  (typeof maybeDate === 'string' && moment(maybeDate).isValid())

/** A TypeScript type guard to determine if a value is a valid ISO-8601 day of week value. */
export const isIso6801Day = (maybeDay: unknown): maybeDay is TIso8601Day =>
  ISO_8601_ALL_DAYS.includes(maybeDay as TIso8601Day)

/** A TypeScript type guard to determine if a value is an array of valid ISO-8601 day of week values. */
export const isIso8601Days = (maybeDays: unknown): maybeDays is TIso8601Day[] =>
  Array.isArray(maybeDays) && maybeDays.length > 0 && maybeDays.every(day => isIso6801Day(day))

/**
 * Formats a city and state code into a formatted string, if possible.
 * @returns A city and state string, else an empty string if missing state code.
 *
 * @example formatCityState({ city: 'akron', stateCode: 'oh'}) // 'Akron, OH'
 * @example formatCityState({ city: 'akron', stateCode: ''}) // ''
 * @example formatCityState({ city: '', stateCode: 'oh'}) // 'OH'
 */
export const formatCityState = ({
  city,
  stateCode,
}: {
  city?: string | null
  stateCode?: string | null
}): string => {
  if (!stateCode) return ''
  let cityStateStr = stateCode.toUpperCase()
  if (city) cityStateStr = `${titleCase(city)}, ${cityStateStr}`
  return cityStateStr
}

/**
 * Formats a radius value into a formatted string, if possible.
 * @returns A radius string, rounded to whole miles, else an empty string if the input is invalid.
 *
 * @example formatRadius(100) // '+100'
 * @example formatRadius(0) // undefined
 * @example formatRadius(undefined) // undefined
 */
export function formatRadius(radius?: string | number | null): string {
  const radiusInt = Math.floor(parseInt(radius as string, 10))
  return radiusInt > 0 ? `+${radiusInt}` : ''
}

/**
 * Formats a city, state, and radius into a formatted lane
 *
 * @example formatLane({ city: 'New York', stateCode: 'NY', radius: 50 }) // 'New York, NY +50'
 * @example formatLane({ city: 'New York', stateCode: 'NY', radius: null }) // 'New York, NY'
 */
export function formatLane({
  city,
  radius,
  stateCode,
}: {
  city: Maybe<string>
  stateCode: Maybe<string>
  radius: Maybe<number>
}) {
  const location = `${formatCityState({
    city,
    stateCode,
  })}`
  if (!radius || radius <= 0) {
    return `${location}`
  }
  return `${location} ${formatRadius(radius)}`
}

/**
 * Formats a date-like object into a formatted string, if possible.
 * @returns A human-readable date string, or an empty string if the input is invalid.
 *
 * @example formatRelativeDate(moment().subtract(5, 'minutes')) // 'Last updated 5 minutes ago'
 * @example formatRelativeDate(moment('05/23/2020')) // 'Last updated 1 year ago'
 * @example formatRelativeDate(undefined) // undefined
 * @example formatRelativeDate('yolo') // undefined
 */
export const formatRelativeDate = (date: TDateLike): string => {
  if (!isDateLike(date)) return ''
  const mDate = moment(date)
  return mDate.isValid() ? mDate.fromNow() : ''
}

// const isIso8601 = (day?: number): day is typeof ISO_8601_DAYS[number] =>
//   [1, 2, 3, 4, 5, 6, 7].includes(day as number)

/**
 * Formats a list of ISO-8601 encoded days of the week into a formatted string, if possible.
 * @param day an ISO-8601 encoded day of the week; e.g. 1-7
 * @returns an abbreviated day of the week; e.g. 'Mon', 'Tue', etc., or an empty string if the input is invalid.
 */
export const formatDay = (day?: number): string =>
  isIso6801Day(day) ? titleCase(moment().day(day).format('ddd')) : ''

/**
 * Formats a list of ISO-8601 encoded days of the week into a formatted string, if possible.
 * @returns A human-readable list of days, or an empty string if the input is invalid.
 *
 * @example formatDays([1, 2, 3, 4, 5]) // 'Weekdays'
 * @example formatDays([6, 7]) // 'Weekends'
 * @example formatDays([1, 2, 3, 4, 5, 6, 7]) // 'Any Day'
 * @example formatDays([1, 6]) // 'Mon, Sat'
 * @example formatDays(undefined) // ''
 */
export const formatDays = (days?: number[]): string => {
  if (!days || !isIso8601Days(days)) return ''
  const daysSet = new Set(days)
  const daysAll = new Set(ISO_8601_ALL_DAYS)
  if (isEqual(daysSet, daysAll)) {
    return 'Any Day'
  }
  const daysWeek = new Set(ISO_8601_WEEK_DAYS)
  if (isEqual(daysSet, daysWeek)) {
    return 'Weekdays'
  }
  const daysWeekend = new Set(ISO_8601_WEEKEND_DAYS)
  if (isEqual(daysSet, daysWeekend)) {
    return 'Weekends'
  }
  return days.map(day => formatDay(day)).join(', ')
}

/**
 * Constructs and formats a pickup availability string based off of the provided equipment name and availability.
 * @returns A human-readable pickup availability string, or an empty string if the input is invalid.
 *
 * @example formatPickupAvailability({ equipmentName: 'reefer', pickupDays: [1, 2, 3, 4, 5] }) // 'Reefer · weekdays'
 * @example formatPickupAvailability({ equipmentName: 'dry van', pickupDays: Date.new() }) // 'Reefer · 11/3'
 */
export const formatPickupAvailability = ({
  days,
  equipmentName,
}: {
  equipmentName?: string | null
  days?: number[] | TDateLike
}): string => {
  const availabilityParts = []
  if (equipmentName) {
    availabilityParts.push(titleCase(equipmentName))
  } else {
    availabilityParts.push('Any Equipment')
  }
  if (isIso8601Days(days)) {
    availabilityParts.push(`${formatDays(days)}`)
  } else if (isDateLike(days)) {
    availabilityParts.push(`${moment(days).format('M/D')}`)
  } else {
    availabilityParts.push('Any Day')
  }
  return availabilityParts.join(DELIM)
}
